import { useState, useEffect, useCallback } from 'react';

import PropTypes from 'prop-types';
import { BsPencil, BsPlus, BsTrash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import RichTextEditor from '#globalComponents/RichTextEditor/RichTextEditor';
import { useError } from '#globalContexts/ErrorContext';
import { sanitizeHtml } from '#lib/sanitize';

import { useCreateSignature, useUpdateSignature, useDeleteSignature } from '../../../models/signatures/mutations';
import { useAllSignatures } from '../../../models/signatures/queries';

const SignatureManagerModal = ({ isOpen, onClose, selectedSignatureId, onSignatureChange }) => {
	const { data: signatures = [], isLoading } = useAllSignatures();
	const createMutation = useCreateSignature();
	const updateMutation = useUpdateSignature();
	const deleteMutation = useDeleteSignature();
	const { showSuccess, showError } = useError();

	const [activeId, setActiveId] = useState(null);
	const [name, setName] = useState('');
	const [isDefault, setIsDefault] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	// The effects below must react to the editor becoming available, so it lives in state.
	const [editor, setEditor] = useState(null);

	// Load signature data when selecting
	const loadSignature = useCallback(
		(sig) => {
			setActiveId(sig.id);
			setName(sig.name);
			setIsDefault(sig.isDefault);
			setIsCreating(false);
			editor?.commands.setContent(sig.content || '');
		},
		[editor],
	);

	// Closing the modal unmounts (and destroys) the editor. Reset the selection so reopening
	// runs the auto-select below again once the fresh editor instance has arrived.
	useEffect(() => {
		if (!isOpen) {
			setActiveId(null);
			setIsCreating(false);
		}
	}, [isOpen]);

	// Select first signature or the pre-selected one on open
	useEffect(() => {
		if (!isOpen || signatures.length === 0 || !editor) return;
		if (isCreating) return;

		const target = signatures.find((s) => s.id === (activeId || selectedSignatureId)) || signatures[0];
		if (target && target.id !== activeId) {
			loadSignature(target);
		}
	}, [isOpen, signatures, editor, selectedSignatureId, activeId, isCreating, loadSignature]);

	const handleCreate = () => {
		setActiveId(null);
		setName('');
		setIsDefault(false);
		setIsCreating(true);
		editor?.commands.setContent('');
		// Delay focus so the DOM settles before browser extensions scan it
		requestAnimationFrame(() => editor?.commands.focus());
	};

	const handleSave = async () => {
		if (!name.trim()) {
			showError({ title: 'Fehler', message: 'Bitte geben Sie einen Namen ein.' });
			return;
		}

		const content = editor?.getHTML() || '';

		try {
			if (isCreating) {
				const { data, error } = await createMutation.mutateAsync({ name: name.trim(), content, isDefault });
				if (error) throw error;
				showSuccess({
					title: 'Signatur erstellt',
					message: 'Die Signatur wurde erfolgreich erstellt.',
					autoClose: true,
				});
				if (data) {
					setActiveId(data.id);
					setIsCreating(false);
					if (!selectedSignatureId || isDefault) onSignatureChange?.(data.id);
				}
			} else {
				const { error } = await updateMutation.mutateAsync({
					id: activeId,
					updates: { name: name.trim(), content, isDefault },
				});
				if (error) throw error;
				showSuccess({
					title: 'Signatur gespeichert',
					message: 'Die Signatur wurde aktualisiert.',
					autoClose: true,
				});
			}
		} catch {
			showError({ title: 'Fehler', message: 'Die Signatur konnte nicht gespeichert werden.' });
		}
	};

	const handleDelete = async (sig) => {
		if (!window.confirm(`Signatur "${sig.name}" wirklich löschen?`)) return;

		try {
			const { error } = await deleteMutation.mutateAsync(sig.id);
			if (error) throw error;
			showSuccess({ title: 'Signatur gelöscht', message: 'Die Signatur wurde gelöscht.', autoClose: true });

			if (selectedSignatureId === sig.id) onSignatureChange?.(null);
			if (activeId === sig.id) {
				const remaining = signatures.filter((s) => s.id !== sig.id);
				if (remaining.length > 0) loadSignature(remaining[0]);
				else handleCreate();
			}
		} catch {
			showError({ title: 'Fehler', message: 'Die Signatur konnte nicht gelöscht werden.' });
		}
	};

	const isSaving = createMutation.isPending || updateMutation.isPending;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="l">
			<Panel.Header onClose={onClose} subtitle="Erstellen und bearbeiten Sie E-Mail-Signaturen.">
				Signaturen verwalten
			</Panel.Header>

			<Panel.Body>
				<div className="grid grid-cols-[240px_1fr] gap-5 min-h-[400px]">
					<div className="border border-bgBase-400 rounded-card overflow-hidden bg-bgBase-200/30">
						<div className="p-3 border-b border-bgBase-400">
							<button
								type="button"
								onClick={handleCreate}
								className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-primary-600 bg-primary-100 hover:bg-primary-100 rounded-control transition-colors"
							>
								<BsPlus className="w-3.5 h-3.5" />
								Neue Signatur
							</button>
						</div>

						<div className="overflow-y-auto max-h-[350px]">
							{isLoading ? (
								<div className="flex justify-center py-8">
									<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
								</div>
							) : signatures.length === 0 && !isCreating ? (
								<p className="text-xs text-bgBase-800 text-center py-8 px-3">
									Noch keine Signaturen vorhanden
								</p>
							) : (
								<div className="p-2 space-y-1">
									{signatures.map((sig) => (
										<div
											key={sig.id}
											onClick={() => loadSignature(sig)}
											className={`group flex items-center justify-between gap-2 px-3 py-2.5 rounded-card cursor-pointer transition-colors ${
												activeId === sig.id && !isCreating
													? 'bg-primary-100 border border-primary-200'
													: 'hover:bg-bgBase-400 border border-transparent'
											}`}
										>
											<div className="min-w-0 flex-1">
												<p className="text-sm font-medium text-text truncate">{sig.name}</p>
												<p
													className="text-[10px] text-bgBase-800 truncate mt-0.5"
													dangerouslySetInnerHTML={{
														__html: sanitizeHtml(
															sig.content?.replace(/<[^>]*>/g, ' ').substring(0, 40) +
																'...',
														),
													}}
												/>
											</div>
											<div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
												{sig.isDefault && (
													<span className="text-[9px] font-medium text-primary-600 bg-primary-100 px-1.5 py-0.5 rounded-full mr-1">
														Standard
													</span>
												)}
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														handleDelete(sig);
													}}
													className="p-1 text-bgBase-800 hover:text-warning-500 rounded transition-colors"
													title="Löschen"
													disabled={deleteMutation.isPending}
												>
													<BsTrash className="w-3 h-3" />
												</button>
											</div>
											{sig.isDefault && (
												<span className="text-[9px] font-medium text-primary-600 bg-primary-100 px-1.5 py-0.5 rounded-full flex-shrink-0 group-hover:hidden">
													Standard
												</span>
											)}
										</div>
									))}
								</div>
							)}
						</div>
					</div>

					{/* The editor form is hidden (not unmounted) without a selection, so the TipTap
					    instance exists before the auto-select effect needs it. */}
					<div className="flex flex-col">
						<div className={activeId || isCreating ? 'flex flex-col flex-1' : 'hidden'}>
							<div className="mb-3">
								<label className="block text-xs font-medium text-bgBase-800 mb-1.5">Name</label>
								<TextInput
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="z.B. Geschäftliche Signatur"
								/>
							</div>

							<div className="flex-1 flex flex-col mb-3">
								<label className="block text-xs font-medium text-bgBase-800 mb-1.5">Inhalt</label>
								<RichTextEditor placeholder="Signatur-Inhalt eingeben ..." onEditorReady={setEditor} />
							</div>

							<div className="flex items-center gap-2 mb-4">
								<Checkbox
									id="sig-default"
									checked={isDefault}
									onChange={(checked) => setIsDefault(checked)}
								/>
								<label htmlFor="sig-default" className="text-sm text-text cursor-pointer">
									Als Standard-Signatur festlegen
								</label>
							</div>

							<div className="flex justify-end gap-2 pt-3 border-t border-bgBase-400">
								<Button variant="outline" size="sm" onClick={onClose}>
									Schließen
								</Button>
								<Button
									size="sm"
									onClick={handleSave}
									loading={isSaving}
									disabled={isSaving || !name.trim()}
								>
									{isCreating ? 'Erstellen' : 'Speichern'}
								</Button>
							</div>
						</div>
						{!activeId && !isCreating && (
							<div className="flex-1 flex items-center justify-center">
								<div className="text-center">
									<div className="w-12 h-12 bg-bgBase-400 rounded-card mx-auto mb-3 grid place-items-center">
										<BsPencil className="w-5 h-5 text-bgBase-800" />
									</div>
									<p className="text-sm text-bgBase-800">
										Wählen Sie eine Signatur aus oder erstellen Sie eine neue.
									</p>
								</div>
							</div>
						)}
					</div>
				</div>
			</Panel.Body>
		</Modal>
	);
};

SignatureManagerModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	selectedSignatureId: PropTypes.string,
	onSignatureChange: PropTypes.func,
};

export default SignatureManagerModal;
